import { useEffect, useCallback, useRef } from "react";
import { useToasts } from "react-toast-notifications";
import { Notification } from "@shared/interfaces";

const PRIORITIES: Record<Notification["appearance"], number> = {
  error: 0,
  warning: 1,
  info: 2,
  success: 3,
};

const QUEUE_CLEANUP_DELAY = 10_000;

const useNotification = (notification: Notification | null) => {
  const { addToast } = useToasts();
  const queueRef = useRef<Notification[]>([]);
  const processingRef = useRef(false);
  const currentIndexRef = useRef(0);
  const cleanupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelCleanup = useCallback(() => {
    if (cleanupTimeoutRef.current != null) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
  }, []);

  const scheduleCleanup = useCallback(() => {
    cancelCleanup();

    cleanupTimeoutRef.current = setTimeout(() => {
      queueRef.current = [];
      currentIndexRef.current = 0;
      cleanupTimeoutRef.current = null;
    }, QUEUE_CLEANUP_DELAY);
  }, [cancelCleanup]);

  const dequeue = useCallback(() => {
    const queue = queueRef.current;

    if (currentIndexRef.current >= queue.length) {
      processingRef.current = false;
      scheduleCleanup();
      return;
    }

    processingRef.current = true;

    const next = queue[currentIndexRef.current];

    currentIndexRef.current += 1;

    addToast(next.message, {
      appearance: next.appearance,
      autoDismiss: true,
      onDismiss: () => {
        dequeue();
      },
    });
  }, [addToast, scheduleCleanup]);

  const enqueue = useCallback(
    (notification: Notification) => {
      cancelCleanup();

      if (!processingRef.current) {
        queueRef.current.push(notification);
        queueRef.current.sort((a, b) => (PRIORITIES[a.appearance] ?? 100) - (PRIORITIES[b.appearance] ?? 100));

        dequeue();
      }
    },
    [dequeue, cancelCleanup],
  );

  useEffect(() => {
    if (notification) {
      enqueue(notification);
    }
  }, [notification, enqueue]);
};

export default useNotification;
